import { stageCaption, stageCardProps, stageStepLabel } from "@/components/agents/configuration-server/stages/centralState";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { getScopeApproval } from "@/api/scope-activation";
import { ConfigServerAlert } from "@/components/agents/configuration-server/ConfigServerAlert";
import { ScopePrerequisiteBanner } from "@/components/agents/configuration-server/ScopePrerequisiteBanner";
import { OverallProgress } from "@/components/agents/configuration-server/stages/OverallProgress";
import { StageCard } from "@/components/agents/configuration-server/stages/StageCard";
import { CompactAgentGlyph } from "@/components/brand/CompactAgentGlyph";
import { HeroOverline } from "@/components/brand/HeroOverline";
import { HeroSection } from "@/components/brand/HeroSection";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePipelineStatus } from "@/hooks/useApi";
import { useConfigServerOverview } from "@/hooks/useConfigServerStages";

export default function ConfigurationServer() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { data: overview, isError, refetch } = useConfigServerOverview(projectId);
  const { data: scopeApproval, isError: scopeError, refetch: refetchScope } = useQuery({
    queryKey: ["scope-approval", projectId], queryFn: () => getScopeApproval(projectId!), enabled: !!projectId,
  });
  const { data: pipeline, isError: pipelineError, refetch: refetchPipeline } = usePipelineStatus(projectId);
  const latestExecution = pipeline?.agents.find((agent) => agent.agent_id === "configuration_server")?.latest_execution;
  const live = !!overview?.active_run;

  const failedReads: Array<() => unknown> = [];
  if (isError) failedReads.push(refetch);
  if (scopeError) failedReads.push(refetchScope);
  if (pipelineError) failedReads.push(refetchPipeline);
  const retryFailedReads = () => failedReads.forEach((retry) => void retry());
  const canStartExtraction = failedReads.length === 0 && !!overview && !overview.has_completed_items
    && !overview.active_run && scopeApproval?.approved === true;

  return <div className="relative -m-6 flex-1 space-y-4">
    <HeroSection padding="compact">
      <Button variant="ghost" size="sm"
        className="-ml-2 mb-2 h-7 gap-1 text-xs text-white/80 hover:bg-white/10 hover:text-white focus-visible:ring-white"
        onClick={() => navigate(`/projects/${projectId}`)}>
        <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />{t("Back to Project")}
      </Button>
      <div className="flex items-start gap-3.5">
        <CompactAgentGlyph number={3} live={live} />
        <div className="min-w-0">
          <HeroOverline live={live} className="mb-1.5">{t("AGENT 03 OF 08")}</HeroOverline>
          <h1 className="m-0 mb-2 break-words font-brand text-[26px] font-extrabold leading-[1.05] tracking-[-0.025em] text-white">{t("Configuration Server")}</h1>
          <p className="mb-2 max-w-2xl text-xs leading-relaxed text-white/80">
            {t("Extracts the configuration from ECC/S/4HANA Private, lets you decide the scope of each item and configures the approved ones in S/4HANA Cloud Public Edition.")}
          </p>
        </div>
      </div>
    </HeroSection>
    {failedReads.length > 0 && <div className="px-6"><ConfigServerAlert message={t("Could not load this page's data.")} onRetry={retryFailedReads} /></div>}
    {!pipelineError && latestExecution?.status === "failed" && projectId && <div className="px-6">
      <ConfigServerAlert
        message={canStartExtraction
          ? t("The last extraction did not finish. Click Retry to start a new one.")
          : t("The last extraction did not finish.")}
        onRetry={canStartExtraction
          ? () => navigate(`/projects/${projectId}/agents/configuration-server/extraction`)
          : undefined}
      />
    </div>}
    {!scopeError && scopeApproval?.approved === false && projectId && <div className="px-6"><ScopePrerequisiteBanner projectId={projectId} /></div>}
    <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-5 px-6 pb-6 pt-2">
      <p role="status" aria-label={t("Overall progress")} aria-live="polite" aria-atomic="true" className="sr-only">
        {!isError && overview ? stageCaption(overview, t) : ""}
      </p>
      {isError ? null : overview && projectId ? <>
        <section className="relative rounded-xl border border-[var(--exed-card-border)] bg-[var(--exed-card-bg)] px-7 pb-7 pt-6 shadow-[0_1px_2px_rgba(8,37,76,0.04)]">
          <span aria-hidden="true" className="absolute -top-px left-7 h-[3px] w-10 rounded-b-[2px] bg-[var(--exed-gold)]" />
          <OverallProgress
            progress={overview.progress}
            stepLabel={stageStepLabel(overview, t)}
            description={stageCaption(overview, t)}
          />
        </section>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
          {stageCardProps(overview, projectId, t).map((card) => <StageCard key={card.number} {...card} />)}
        </div>
      </> : <div aria-busy="true" aria-label={t("Loading…")} className="space-y-4">
        <Skeleton aria-hidden="true" className="h-36 w-full rounded-xl" />
        <div aria-hidden="true" className="grid gap-5 lg:grid-cols-3">
          <Skeleton className="h-80 rounded-xl" /><Skeleton className="h-80 rounded-xl" /><Skeleton className="h-80 rounded-xl" />
        </div>
      </div>}
    </div>
  </div>;
}
